import type {
  StructuredFilterGroupRequest,
  StructuredFilterRequest,
} from "@/components/StructuredSearch";
import { buildDateRangeFilter } from "@/routes/request-logs/search/structured-search-payload";
import type { CompleteDateRange } from "@/components/StructuredSearch/stores/structured-search/structured-search-store";
import type { CustomChartSpecRequest } from "@/queries/analytics-custom-charts-types";

const DATE_RANGE_FIELD = "request_start_time";
export const REQUEST_ANALYTICS_DATE_RANGE_FIELD = DATE_RANGE_FIELD;
export const TRACE_ANALYTICS_DATE_RANGE_FIELD = "trace_start";

const isStructuredFilterRequest = (
  filter: StructuredFilterRequest | StructuredFilterGroupRequest,
): filter is StructuredFilterRequest => "field" in filter;

const isDateRangeFilter = (
  filter: StructuredFilterRequest,
  dateRangeField: string = DATE_RANGE_FIELD,
): boolean => filter.field === dateRangeField && filter.operator === "between";

const parseCompleteDateRange = (
  value: StructuredFilterRequest["value"],
): CompleteDateRange | undefined => {
  if (!Array.isArray(value) || value.length !== 2) {
    return undefined;
  }

  const from = new Date(String(value[0]));
  const to = new Date(String(value[1]));
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return undefined;
  }

  return { from, to };
};

export const getDateRangeFromFilterGroup = (
  filterGroup?: StructuredFilterGroupRequest,
  dateRangeField: string = DATE_RANGE_FIELD,
): CompleteDateRange | undefined => {
  if (!filterGroup) {
    return undefined;
  }

  for (const filter of filterGroup.filters) {
    if (isStructuredFilterRequest(filter)) {
      if (isDateRangeFilter(filter, dateRangeField)) {
        return parseCompleteDateRange(filter.value);
      }
      continue;
    }

    const nestedDateRange = getDateRangeFromFilterGroup(filter, dateRangeField);
    if (nestedDateRange) {
      return nestedDateRange;
    }
  }

  return undefined;
};

export const stripDateRangeFromFilterGroup = (
  filterGroup?: StructuredFilterGroupRequest,
  dateRangeField: string = DATE_RANGE_FIELD,
): StructuredFilterGroupRequest | undefined => {
  if (!filterGroup) {
    return undefined;
  }

  const filters = filterGroup.filters
    .map(filter => {
      if (isStructuredFilterRequest(filter)) {
        return isDateRangeFilter(filter, dateRangeField) ? null : filter;
      }

      return stripDateRangeFromFilterGroup(filter, dateRangeField);
    })
    .filter(
      (filter): filter is StructuredFilterRequest | StructuredFilterGroupRequest =>
        filter !== null && filter !== undefined,
    );

  if (filters.length === 0) {
    return undefined;
  }

  return {
    logic: filterGroup.logic,
    filters,
  };
};

const replaceDateRangeInFilterGroup = (
  filterGroup: StructuredFilterGroupRequest,
  dateRange: CompleteDateRange,
  options: { dateRangeField?: string; preserveDateRangeLocalOffset?: boolean } = {},
): StructuredFilterGroupRequest => {
  const dateRangeField = options.dateRangeField ?? DATE_RANGE_FIELD;
  const withoutDateRange = stripDateRangeFromFilterGroup(filterGroup, dateRangeField);
  const dateRangeFilter = {
    ...buildDateRangeFilter(dateRange, {
      preserveLocalOffset: options.preserveDateRangeLocalOffset,
    }),
    field: dateRangeField,
  };

  if (!withoutDateRange) {
    return {
      logic: "AND",
      filters: [dateRangeFilter],
    };
  }

  return {
    logic: "AND",
    filters: [withoutDateRange, dateRangeFilter],
  };
};

export const mergeAnalyticsGraphFilterGroups = (
  artifactFilterGroup?: StructuredFilterGroupRequest,
  _pageFilterGroup?: StructuredFilterGroupRequest,
  options?: {
    dateRangeField?: string;
    dateRangeOverride?: CompleteDateRange;
    preserveDateRangeLocalOffset?: boolean;
  },
): StructuredFilterGroupRequest | undefined => {
  if (artifactFilterGroup) {
    if (options?.dateRangeOverride) {
      return replaceDateRangeInFilterGroup(artifactFilterGroup, options.dateRangeOverride, {
        dateRangeField: options.dateRangeField,
        preserveDateRangeLocalOffset: options.preserveDateRangeLocalOffset,
      });
    }
    return artifactFilterGroup;
  }

  if (options?.dateRangeOverride) {
    return {
      logic: "AND",
      filters: [
        {
          ...buildDateRangeFilter(options.dateRangeOverride, {
            preserveLocalOffset: options.preserveDateRangeLocalOffset,
          }),
          field: options.dateRangeField ?? DATE_RANGE_FIELD,
        },
      ],
    };
  }

  return undefined;
};

export interface AnalyticsGraphSearchParams {
  q?: string;
  filterGroup?: StructuredFilterGroupRequest;
  customCharts?: CustomChartSpecRequest[];
}

export const buildAnalyticsGraphSearchParams = ({
  customChartSpec,
  effectiveFilterGroup,
  pageQuery,
  hasArtifactFilterGroup,
}: {
  customChartSpec?: CustomChartSpecRequest;
  effectiveFilterGroup?: StructuredFilterGroupRequest;
  pageQuery?: string;
  hasArtifactFilterGroup?: boolean;
}): AnalyticsGraphSearchParams => ({
  ...(!hasArtifactFilterGroup && pageQuery ? { q: pageQuery } : {}),
  ...(effectiveFilterGroup ? { filterGroup: effectiveFilterGroup } : {}),
  ...(customChartSpec ? { customCharts: [customChartSpec] } : {}),
});
